/**
 * Sales → Rate Cards: the sell-side rate cards (list + detail + editor) and
 * the advertiser registry. Visible to sales.view; every write control needs
 * `mediaPlans.manage`. Uses the existing /rate-cards and /advertisers routes.
 * The reference data and registry come from the dashboard (shared with Media
 * Plans), so an advertiser edited here is the one the plan builder offers.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner, Tabs } from '../../ui';
import { createRateCard, listRateCards, updateRateCard } from '../../../services/mediaPlansApi';
import type { Notify } from '../MediaPlans/PlanBuilder';
import type { MediaPlanRateCard, RateCardStatus } from '../MediaPlans/types';
import type { MediaPlanRegistry } from '../MediaPlans/useMediaPlanRegistry';
import AdvertiserRegistry from './AdvertiserRegistry';
import RateCardEditor from './RateCardEditor';
import RateCardList from './RateCardList';
import RateCardPanel from './RateCardPanel';
import { draftFromRateCard, draftToRateCardInput, newRateCardDraft, validateRateCardDraft, type RateCardDraft } from './rateCardDraft';
import { bannerStyle } from '../MediaPlans/formStyles';

interface Props {
  registry: MediaPlanRegistry;
  canManage: boolean;
  /** Increments when the dashboard's "+ New rate card" button is pressed. */
  addSignal: number;
  notify: Notify;
}

type View = 'cards' | 'advertisers';

/** The editor's draft and what it opened with, to tell whether closing loses anything. */
interface EditorState {
  draft: RateCardDraft;
  opened: string;
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

const RateCardsTab: React.FC<Props> = ({ registry, canManage, addSignal, notify }) => {
  const [view, setView] = useState<View>('cards');
  const [cards, setCards] = useState<MediaPlanRateCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [draftErrors, setDraftErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const lastSignal = useRef(addSignal);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const loadCards = useCallback(async () => {
    setCardsLoading(true);
    setCardsError(null);
    try {
      setCards(await listRateCards());
    } catch (err) {
      setCardsError(errorMessage(err));
    } finally {
      setCardsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCards();
  }, [loadCards]);

  const openEditor = useCallback((draft: RateCardDraft) => {
    setEditor({ draft, opened: JSON.stringify(draft) });
    setDraftErrors([]);
    setConfirmDiscard(false);
  }, []);

  useEffect(() => {
    if (addSignal !== lastSignal.current) {
      lastSignal.current = addSignal;
      if (canManage && !editor) {
        setView('cards');
        openEditor(newRateCardDraft());
      }
    }
  }, [addSignal, canManage, editor, openEditor]);

  // Bring the chosen card's panel into view (it sits above the list).
  useEffect(() => {
    if (selectedId) panelRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  const selected = useMemo(() => cards.find((c) => c.id === selectedId) || null, [cards, selectedId]);
  const reference = registry.reference;
  const categories = reference?.categories.categories || [];
  const agencies = reference?.agencies.agencies || [];
  const units = reference?.units || [];

  const upsertCard = (card: MediaPlanRateCard) => setCards((prev) => (prev.some((c) => c.id === card.id) ? prev.map((c) => (c.id === card.id ? card : c)) : [...prev, card]));

  const requestCloseEditor = () => {
    if (editor && JSON.stringify(editor.draft) !== editor.opened) setConfirmDiscard(true);
    else setEditor(null);
  };

  const save = async () => {
    if (!editor) return;
    const { draft } = editor;
    const errors = validateRateCardDraft(draft);
    setDraftErrors(errors);
    if (errors.length) return;
    setSaving(true);
    try {
      const input = draftToRateCardInput(draft);
      const saved = draft.id ? await updateRateCard(draft.id, input) : await createRateCard(input);
      upsertCard(saved);
      setSelectedId(saved.id);
      setEditor(null);
      notify('success', draft.id ? 'Rate card updated' : 'Rate card created', saved.name);
    } catch (err) {
      setDraftErrors([errorMessage(err)]);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (card: MediaPlanRateCard, status: RateCardStatus) => {
    setBusy(true);
    try {
      const { id, version, createdBy, updatedBy, createdAt, updatedAt, ...rest } = card;
      const saved = await updateRateCard(card.id, { ...rest, status });
      upsertCard(saved);
      notify('success', status === 'active' ? 'Rate card activated' : `Rate card ${status}`, saved.name);
    } catch (err) {
      notify('error', 'Could not update the rate card', errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!reference) {
    return registry.error ? (
      <div role="alert" style={bannerStyle('danger')}>
        <span style={{ flex: 1 }}>Could not load rate cards: {registry.error}</span>
        <Button size="sm" variant="ghost" onClick={registry.reload} disabled={registry.loading}>
          {registry.loading ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    ) : (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Spinner label="Loading rate cards…" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Tabs<View>
          value={view}
          onChange={setView}
          options={[
            { value: 'cards', label: 'Rate cards', count: cards.length },
            { value: 'advertisers', label: 'Advertiser registry', count: registry.advertisers.length },
          ]}
          size="sm"
          ariaLabel="Rate card sections"
        />
        {view === 'cards' && canManage && (
          <Button size="sm" variant="subtle" onClick={() => openEditor(newRateCardDraft())} disabled={!!editor}>
            + New rate card
          </Button>
        )}
      </div>

      {view === 'cards' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {cardsError && (
            <div role="alert" style={bannerStyle('danger')}>
              <span style={{ flex: 1 }}>Could not load the rate cards: {cardsError}</span>
              <Button size="sm" variant="ghost" onClick={() => void loadCards()} disabled={cardsLoading}>
                Retry
              </Button>
            </div>
          )}
          {selected && (
            <div ref={panelRef}>
              <RateCardPanel
                key={selected.id}
                card={selected}
                units={units}
                categories={categories}
                agencies={agencies}
                advertisers={registry.advertisers}
                canManage={canManage}
                busy={busy}
                onEdit={() => openEditor(draftFromRateCard(selected))}
                onDuplicate={() => openEditor(draftFromRateCard(selected, { duplicate: true }))}
                onSetStatus={(status) => void setStatus(selected, status)}
                onClose={() => setSelectedId(null)}
              />
            </div>
          )}
          {cardsLoading && cards.length === 0 ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
              <Spinner label="Loading rate cards…" />
            </div>
          ) : (
            <RateCardList cards={cards} categories={categories} agencies={agencies} advertisers={registry.advertisers} selectedId={selectedId} onSelect={setSelectedId} />
          )}
        </div>
      ) : (
        <AdvertiserRegistry
          advertisers={registry.advertisers}
          categories={categories}
          agencies={agencies}
          cards={cards}
          canManage={canManage}
          onUpdated={registry.upsertAdvertiser}
          notify={notify}
        />
      )}

      {editor && (
        <RateCardEditor
          draft={editor.draft}
          onChange={(draft) => setEditor((e) => (e ? { ...e, draft } : e))}
          onSave={() => void save()}
          onCancel={requestCloseEditor}
          confirmDiscard={confirmDiscard}
          onDiscard={() => {
            setConfirmDiscard(false);
            setEditor(null);
          }}
          onKeepEditing={() => setConfirmDiscard(false)}
          saving={saving}
          errors={draftErrors}
          units={units}
          categories={categories}
          agencies={agencies}
          advertisers={registry.advertisers}
        />
      )}
    </div>
  );
};

export default RateCardsTab;
